/* eslint-disable @typescript-eslint/no-unused-vars */
import { err, JsonDecoder, ok } from 'ts.data.json';
import { $JsonDecoderErrors } from 'ts.data.json/dist/json-decoder';
import { EatEventFileContent, EatEventFileMetadata, PersonVideoMapping } from 'types/EatEventFileContent';
import { v4 as uuidv4 } from 'uuid';
import { EatEventWithId } from 'types/EatEvent';

/** **********************
 * Useful Array Decoders *
 *********************** */
export const stringArrayDecoder = JsonDecoder.array<string>(JsonDecoder.string, 'string[]');

export const stringOrNumberDecoder = JsonDecoder.oneOf<string | number>(
    [JsonDecoder.string, JsonDecoder.number],
    'string | number'
);

export const stringOrNumberArrayDecoder = JsonDecoder.array<string | number>(
    stringOrNumberDecoder,
    'Array<string|number>[]'
);

/** *****
 * Date *
 ****** */
const dateDecoder: JsonDecoder.Decoder<Date> = new JsonDecoder.Decoder<Date>((json: unknown) => {
    if (typeof json === 'string') {
        const isValidDate = !isNaN(Date.parse(json));

        if (isValidDate) {
            return ok<Date>(new Date(json));
        }
    }

    return err<Date>($JsonDecoderErrors.primitiveError(json, 'Date'));
});

/** **
 * UUID
 */
const uuidFailoverDecoder: JsonDecoder.Decoder<string> = new JsonDecoder.Decoder<string>((json: unknown) => {
    if (json === undefined) {
        return ok<string>(uuidv4());
    }

    if (typeof json === 'string') {
        // Verify that json string is a valid UUID.
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(json);

        if (isUuid) {
            return ok<string>(json);
        }
    }

    return err<string>($JsonDecoderErrors.primitiveError(json, 'UUID'));
});

/** ****************
 * Interestingness *
 ***************** */

const eatEventDecoder = JsonDecoder.object<EatEventWithId>(
    {
        id: uuidFailoverDecoder,
        person: JsonDecoder.string,
        hand: JsonDecoder.string,
        time_start: JsonDecoder.number,
        time_end: JsonDecoder.number,
        assessment: JsonDecoder.optional(JsonDecoder.number),
        skeleton_confidence: JsonDecoder.optional(JsonDecoder.number),
        event_type: JsonDecoder.optional(JsonDecoder.number),
    },
    'EatEvent'
);

const personVideoMappingDecoder = JsonDecoder.object<PersonVideoMapping>(
    {
        person: JsonDecoder.string,
        video: JsonDecoder.string,
    },
    'PersonVideoMapping'
);

const eatEventFileMetadataDecoder = JsonDecoder.object<EatEventFileMetadata>(
    {
        person_video_mappings: JsonDecoder.array(personVideoMappingDecoder, 'PersonVideoMapping[]'),
    },
    'EatEventFileMetadata'
);

export const eatEventFileContentDecoder = JsonDecoder.object<EatEventFileContent>(
    {
        metadata: eatEventFileMetadataDecoder,
        events: JsonDecoder.array<EatEventWithId>(eatEventDecoder, 'EatEvent[]'),
    },
    'EatEventFileContent'
);
